import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Base from '../Base';
import Card from '../../components/elements/Card';
import Title from '../../components/common/Title';
import Back from '../../components/common/Back';
import Icon from '../../components/common/Icon';
import { TagChips, TagPills } from '../../components/common/EventTags';
import { apiRequest } from '../../utils';
import './MemberHome.css';
import './CompanyEvents.css';
import './Clubs.css';

/* The club directory — every club of my company, and where I stand in each.

   Open to everybody on the company's roster: a club recruits by being seen. Joining is a request
   the club's admins answer, so the card says "Requested" until they do. The company admin gets
   the way in to founding one at the top. */

export const ClubStatus = ({ club }) => {
  const { t } = useTranslation();
  if (club.is_archived) return <span className="club-status club-status--archived">{t('clubArchived')}</span>;
  if (club.my_role === 'admin') return <span className="club-status">{t('clubYouAreAdmin')}</span>;
  if (club.my_role === 'member') return <span className="club-status">{t('clubYouAreMember')}</span>;
  if (club.requested) return <span className="club-status club-status--requested">{t('clubRequested')}</span>;
  return null;
};

export const ClubImage = ({ club, className }) => (
  club.image
    ? <img className={className} src={club.image} alt="" />
    : <div className={className}><Icon name="users" theme="primary" /></div>
);

const Clubs = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [picked, setPicked] = useState([]);

  useEffect(() => {
    apiRequest('GET', '/clubs').then(setData).catch(() => navigate('/member-home'));
  }, [navigate]);

  const toggleTag = (id) => setPicked((list) =>
    (list.includes(id) ? list.filter((x) => x !== id) : [...list, id]));

  const clubs = ((data && data.clubs) || []).filter((c) => !picked.length
    || (c.tags || []).some((tag) => picked.includes(String(tag.id).toLowerCase())
                                    || picked.includes(tag.id)));
  /* Only the categories some club actually carries — a chip that filters to nothing is noise. */
  const used = new Set(((data && data.clubs) || []).flatMap((c) => (c.tags || []).map((x) => String(x.id).toLowerCase())));
  const tags = ((data && data.tags) || []).filter((x) => used.has(String(x.id).toLowerCase()));

  return (
    <Base activeNav="home">
      <Card>
        <Back to="/member-home" />
        <Title my="1" className="mt-3">
          <Icon name="users" theme="primary" className="me-2" />
          {t('clubsTitle')}
        </Title>
        <p className="bravos-q__hint mb-2">{t('clubsNote')}</p>

        {data && data.can_create && (
          <button type="button" className="bravos-submit bravos-submit--ghost"
                  onClick={() => navigate('/clubs/manage')}>
            <Icon name="users-gear" /> {t('clubsManage')} <Icon name="angle-right" />
          </button>
        )}

        {data === null ? <span className="auth__spinner" /> : (
          <>
            {tags.length > 1 && (
              <div className="ce-tagrow">
                <TagChips tags={tags} selected={picked} onToggle={toggleTag} />
              </div>
            )}
            {clubs.length === 0 && (
              <p className="ce-note">{data.can_create ? t('clubsEmptyAdmin') : t('clubsEmpty')}</p>
            )}
            <div className="club-grid">
              {clubs.map((club) => (
                <button key={club.id} type="button" className="club-card"
                        onClick={() => navigate(`/clubs/${club.id}`)}>
                  <ClubImage club={club} className="club-card__img" />
                  <div className="club-card__body">
                    <p className="club-card__name">{club.name}</p>
                    {club.description && <p className="club-card__desc">{club.description}</p>}
                    <TagPills tags={club.tags || []} />
                    <div className="club-card__meta">
                      <span><Icon name="users" className="me-1" />{t('clubMembersN', { n: club.member_count })}</span>
                      {club.upcoming_count > 0 && (
                        <span><Icon name="calendar" className="me-1" />{t('clubUpcomingN', { n: club.upcoming_count })}</span>
                      )}
                    </div>
                    <ClubStatus club={club} />
                  </div>
                </button>
              ))}
            </div>
          </>
        )}
      </Card>
    </Base>
  );
};

export default Clubs;
